import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import apiCallManager from '../utils/apiCallManager';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Clock,
  User,
  Mail,
  FileText,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Calendar,
  BarChart3
} from 'lucide-react';
import './SLAMonitoringDashboard.css';
import type {
  SLAMonitoringDashboardProps,
  SLAFilters,
  SLAPagination,
  SLAConfig,
  SortConfig,
  SLAThread,
  SLASummary,
  ThreadConversation,
  Employee,
} from '../types';

// Debug: Check if CSS is loaded
console.log('SLA Monitoring: CSS imported, checking component structure');

const SLAMonitoringDashboard: React.FC<SLAMonitoringDashboardProps> = ({ user }) => {
  const [threads, setThreads] = useState<SLAThread[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [summary, setSummary] = useState<SLASummary | null>(null);

  // Filter states
  const [filters, setFilters] = useState<SLAFilters>({
    employeeId: '',
    status: 'assigned',
    slaTime: '12h',
    sentiment: ''
  });

  // Pagination states
  const [pagination, setPagination] = useState<SLAPagination>({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
    hasMore: false
  });

  // SLA configuration
  const [slaConfig, setSlaConfig] = useState<SLAConfig>({
    businessHours: { start: 9, end: 18, timezone: 'Asia/Riyadh' },
    slaThresholds: { '0h': 0, '12h': 12, '24h': 24, '2d': 48, '5d': 120 },
    weekends: ['Friday', 'Saturday']
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'overdueBusinessHours',
    direction: 'asc' // Default: ascending order
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedThread, setSelectedThread] = useState<SLAThread | null>(null);
  const [threadConversations, setThreadConversations] = useState<ThreadConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  // Component initialization state
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Fetch overdue threads
  const fetchOverdueThreads = useCallback(async (page: number = 1, reset: boolean = false): Promise<void> => {
    const callId = `fetchOverdueThreads_${page}_${JSON.stringify(filters)}_${JSON.stringify(sortConfig)}`;

    try {
      console.log('SLA Monitoring: Fetching overdue threads...', { page, reset, filters, sortConfig });
      setLoading(true);

      // Map frontend sort keys to backend sort keys
      const sortByMap: Record<string, string> = {
        'overdueBusinessHours': 'overdueBusinessHours',
        'sentiment': 'sentiment',
        'assignmentDate': 'assignmentDate'
      };

      const backendSortBy = sortByMap[sortConfig.key] || 'assignmentDate';
      const backendSortOrder = sortConfig.direction.toUpperCase();

      const params = {
        ...filters,
        page,
        limit: pagination.limit,
        sortBy: backendSortBy,
        sortOrder: backendSortOrder
      };

      const response = await apiCallManager.execute(callId, () =>
        api.get('/sla-monitoring/overdue', {
          params,
          timeout: backendSortBy === 'overdueBusinessHours' ? 30000 : 10000 // Longer timeout for calculated field
        })
      );

      if (response.data.success) {
        if (reset || page === 1) {
          setThreads(response.data.data);
        } else {
          setThreads(prev => [...prev, ...response.data.data]);
        }

        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalPages: response.data.pagination.totalPages,
            total: response.data.pagination.total,
            hasMore: response.data.pagination.hasMore
          }));
        }
        console.log('SLA Monitoring: Overdue threads fetched successfully');
      }
    } catch (error) {
      console.error('SLA Monitoring: Error fetching overdue threads:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, sortConfig]);

  // Fetch employees for dropdown
  const fetchEmployees = useCallback(async (): Promise<void> => {
    const callId = 'fetchEmployees';

    try {
      console.log('SLA Monitoring: Fetching employees...');

      const response = await apiCallManager.execute(callId, () =>
        api.get('/employees')
      );

      if (response.data.success) {
        setEmployees(response.data.data);
        console.log('SLA Monitoring: Employees fetched successfully');
      }
    } catch (error) {
      console.error('SLA Monitoring: Error fetching employees:', error);
    }
  }, []);

  // Fetch summary statistics
  const fetchSummary = useCallback(async (): Promise<void> => {
    const callId = `fetchSummary_${filters.slaTime}_${filters.sentiment}`;

    try {
      console.log('SLA Monitoring: Fetching summary...', { slaTime: filters.slaTime, sentiment: filters.sentiment });

      const response = await apiCallManager.execute(callId, () =>
        api.get('/sla-monitoring/summary', {
          params: {
            slaTime: filters.slaTime,
            sentiment: filters.sentiment
          }
        })
      );

      if (response.data.success) {
        setSummary(response.data.data);
        console.log('SLA Monitoring: Summary fetched successfully');
      }
    } catch (error) {
      console.error('SLA Monitoring: Error fetching summary:', error);
    }
  }, [filters.slaTime, filters.sentiment]);

  // Initialize component data
  const initializeComponent = useCallback(async (): Promise<void> => {
    if (isInitialized) {
      console.log('SLA Monitoring: Already initialized, skipping...');
      return;
    }

    console.log('SLA Monitoring: Initializing component...');
    setIsInitialized(true);

    try {
      // Execute all API calls in parallel
      await Promise.all([
        fetchEmployees(),
        fetchOverdueThreads(1, true),
        fetchSummary()
      ]);

      console.log('SLA Monitoring: Component initialized successfully');
    } catch (error) {
      console.error('SLA Monitoring: Error during initialization:', error);
      // Reset initialization state on error so it can be retried
      setIsInitialized(false);
    }
  }, [isInitialized, fetchEmployees, fetchOverdueThreads, fetchSummary]);

  // Handle filter changes
  const handleFilterChange = (key: keyof SLAFilters, value: string): void => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const applyFilters = (): void => {
    console.log('SLA Monitoring: Applying filters:', filters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Force refresh to bypass cache when applying filters, preserve current sort order
    fetchOverdueThreadsWithRefresh(1, true, sortConfig);
    fetchSummaryWithRefresh();
  };

  // Fetch overdue threads with force refresh
  const fetchOverdueThreadsWithRefresh = useCallback(async (page: number = 1, reset: boolean = false, overrideSortConfig?: SortConfig | null): Promise<void> => {
    // Use override sortConfig if provided, otherwise use state sortConfig
    const activeSortConfig = overrideSortConfig || sortConfig;
    const callId = `fetchOverdueThreads_${page}_${JSON.stringify(filters)}_${JSON.stringify(activeSortConfig)}`;

    try {
      console.log('SLA Monitoring: Fetching overdue threads (force refresh)...', { page, reset, filters, activeSortConfig, overrideSortConfig });
      setLoading(true);

      // Map frontend sort keys to backend sort keys
      const sortByMap: Record<string, string> = {
        'overdueBusinessHours': 'overdueBusinessHours',
        'sentiment': 'sentiment',
        'assignmentDate': 'assignmentDate'
      };

      const backendSortBy = activeSortConfig?.key ? sortByMap[activeSortConfig.key] || 'assignmentDate' : 'assignmentDate';
      const backendSortOrder = activeSortConfig?.direction ? activeSortConfig.direction.toUpperCase() : 'ASC';
      console.log('Backend sort by', backendSortBy, backendSortOrder);
      const params = {
        ...filters,
        page,
        limit: pagination.limit,
        sortBy: backendSortBy,
        sortOrder: backendSortOrder
      };

      console.log('Sending pagination request with sort params:', { sortBy: backendSortBy, sortOrder: backendSortOrder, page });

      const response = await apiCallManager.execute(callId, () =>
        api.get('/sla-monitoring/overdue', {
          params,
          timeout: backendSortBy === 'overdueBusinessHours' ? 30000 : 10000 // Longer timeout for calculated field
        }), true // Force refresh
      );

      if (response.data.success) {
        if (reset || page === 1) {
          setThreads(response.data.data);
        } else {
          setThreads(prev => [...prev, ...response.data.data]);
        }

        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalPages: response.data.pagination.totalPages,
            total: response.data.pagination.total,
            hasMore: response.data.pagination.hasMore
          }));
        }
        console.log('SLA Monitoring: Overdue threads fetched successfully');
      }
    } catch (error) {
      console.error('SLA Monitoring: Error fetching overdue threads:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, sortConfig]);

  // Fetch summary with force refresh
  const fetchSummaryWithRefresh = useCallback(async (): Promise<void> => {
    const callId = `fetchSummary_${filters.slaTime}_${filters.sentiment}`;

    try {
      console.log('SLA Monitoring: Fetching summary (force refresh)...', { slaTime: filters.slaTime, sentiment: filters.sentiment });

      const response = await apiCallManager.execute(callId, () =>
        api.get('/sla-monitoring/summary', {
          params: {
            slaTime: filters.slaTime,
            sentiment: filters.sentiment
          }
        }), true // Force refresh
      );

      if (response.data.success) {
        setSummary(response.data.data);
        console.log('SLA Monitoring: Summary fetched successfully');
      }
    } catch (error) {
      console.error('SLA Monitoring: Error fetching summary:', error);
    }
  }, [filters.slaTime, filters.sentiment]);

  // Export data
  const exportData = async (): Promise<void> => {
    try {
      setExporting(true);
      await api.post('/sla-monitoring/export', filters);
      const email = user?.email || 'your email';
      alert(`Export requested successfully. The CSV will be sent to ${email} once it is ready.`);
    } catch (error: unknown) {
      console.error('Error exporting data:', error);
      const axiosErr = error as { response?: { data?: { message?: string } }; message?: string };
      const message = axiosErr?.response?.data?.message || axiosErr.message || 'Failed to queue export.';
      alert(`Error exporting data: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  // Handle view thread
  const handleViewThread = async (thread: SLAThread): Promise<void> => {
    try {
      setSelectedThread(thread);
      setIsModalOpen(true);
      setLoadingConversations(true);

      // Fetch thread conversations
      const response = await api.get(`/emails/thread/${thread.threadId}`);
      if (response.data.success) {
        // Sort conversations in descending order (newest first)
        const sortedConversations = response.data.data.sort((a: ThreadConversation, b: ThreadConversation) =>
          new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
        );
        setThreadConversations(sortedConversations);
      }
    } catch (error) {
      console.error('Error fetching thread conversations:', error);
      setThreadConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Handle close thread
  const handleCloseThread = async (thread: SLAThread): Promise<void> => {
    if (window.confirm(`Are you sure you want to close the thread "${thread.subject}"?`)) {
      try {
        // Call API to close thread
        await api.put(`/sla-monitoring/threads/${thread.id}/close`);

        alert('Thread closed successfully!');
      } catch (error) {
        console.error('Error closing thread:', error);
        alert('Failed to close thread. Please try again.');
      }
    }
  };

  // Handle sorting by overdue time
  const handleSortByOverdue = (): void => {
    console.log('Sorting by overdue time, current sortConfig:', sortConfig);
    const newSortConfig: SortConfig = {
      key: 'overdueBusinessHours',
      direction: sortConfig.key === 'overdueBusinessHours' && sortConfig.direction === 'desc' ? 'asc' : 'desc'
    };
    console.log('Sorting by overdue time, modified sortConfig:', newSortConfig);
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Trigger API call with new sort config - pass it directly to avoid stale closure
    fetchOverdueThreadsWithRefresh(1, true, newSortConfig);
  };

  // Handle sorting by sentiment
  const handleSortBySentiment = (): void => {
    const newSortConfig: SortConfig = {
      key: 'sentiment',
      direction: sortConfig.key === 'sentiment' && sortConfig.direction === 'desc' ? 'asc' : 'desc'
    };
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Trigger API call with new sort config - pass it directly to avoid stale closure
    fetchOverdueThreadsWithRefresh(1, true, newSortConfig);
  };

  // Handle close modal
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setSelectedThread(null);
    setThreadConversations([]);
  };

  // Format overdue time
  const formatOverdueTime = (hours: number): string => {
    if (hours < 24) {
      return `${hours}h`;
    } else if (hours < 48) {
      return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    } else {
      return `${Math.floor(hours / 24)}d`;
    }
  };

  // Get overdue status color
  const getOverdueStatusColor = (overdueStatus: string): string => {
    switch (overdueStatus) {
      case 'exceeded_12h':
        return '#f59e0b'; // amber
      case 'exceeded_24h':
        return '#ef4444'; // red
      case 'exceeded_2d':
        return '#dc2626'; // dark red
      case 'exceeded_5d':
        return '#991b1b'; // very dark red
      default:
        return '#6b7280'; // gray
    }
  };

  // Initialize component on mount
  useEffect(() => {
    initializeComponent();
  }, []); // Empty dependency array - only run once on mount

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isModalOpen) {
        handleCloseModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  return (
    <div className="sla-monitoring-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>
            <Clock className="header-icon" />
            SLA Monitoring Dashboard
          </h1>
          <p>Track threads exceeding response time SLAs</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon warning">
              <AlertTriangle />
            </div>
            <div className="card-content">
              <h3>{summary.totalOverdue}</h3>
              <p>Total Overdue</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon info">
              <Clock />
            </div>
            <div className="card-content">
              <h3>{summary.averageOverdueHours}h</h3>
              <p>Avg Overdue</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon danger">
              <AlertTriangle />
            </div>
            <div className="card-content">
              <h3>{summary.byOverdueStatus['5d'] || 0}</h3>
              <p>Critical (5+ Days)</p>
            </div>
          </div>


          <div className="summary-card">
            <div className="card-icon info">
              <BarChart3 />
            </div>
            <div className="card-content">
              <h3>{summary.bySentiment?.positive || 0}</h3>
              <p>Positive Sentiment</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon warning">
              <AlertTriangle />
            </div>
            <div className="card-content">
              <h3>{summary.bySentiment?.negative || 0}</h3>
              <p>Negative Sentiment</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <Filter className="filter-icon" />
          <h3>Filters</h3>
        </div>
        <div className="filters-content">
          <div className="filter-group">
            <label>Employee:</label>
            <select
              value={filters.employeeId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('employeeId', e.target.value)}
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('status', e.target.value)}
            >
              <option value="assigned">Assigned</option>
              <option value="responded">Responded</option>
            </select>
          </div>

          <div className="filter-group">
            <label>SLA Time:</label>
            <select
              value={filters.slaTime}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('slaTime', e.target.value)}
            >
              <option value="0h">0 Hours</option>
              <option value="12h">12 Hours</option>
              <option value="24h">24 Hours</option>
              <option value="2d">2 Days</option>
              <option value="5d">5 Days</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sentiment:</label>
            <select
              value={filters.sentiment}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('sentiment', e.target.value)}
            >
              <option value="">All Sentiments</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
            </select>
          </div>

          <button className="btn btn-primary" onClick={applyFilters}>
            Apply Filters
          </button>
        </div>
      </div>

      {/* Export Button */}
      <div className="export-section">
        <button
          className="btn btn-success"
          onClick={exportData}
          disabled={exporting}
        >
          <Download className="btn-icon" />
          {exporting ? 'Exporting...' : 'Export Data'}
        </button>
        <span className="export-info">
          Exporting {pagination.total} threads as CSV
        </span>
      </div>

      {/* Threads Table */}
      <div className="threads-table-container">
        <div className="table-header">
          <h3>Overdue Threads ({pagination.total})</h3>
          <div className="table-actions">
            <div className="info-cards">
              <div className="info-card">
                <span className="info-label">Business Hours:</span>
                <span className="info-value">{slaConfig.businessHours.start}:00 - {slaConfig.businessHours.end}:00 KSA Time</span>
              </div>
              <div className="info-card">
                <span className="info-label">Weekends:</span>
                <span className="info-value">{slaConfig.weekends?.join(' & ') || 'Friday & Saturday'} excluded</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading overdue threads...</div>
        ) : threads.length === 0 ? (
          <div className="no-data">No overdue threads found</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="threads-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Client Email</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Last Update</th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSortByOverdue()}
                    >
                      Overdue Time
                      <span className="sort-indicator">
                        {sortConfig.key === 'overdueBusinessHours'
                          ? (sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193')
                          : ' \u2191'}
                      </span>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSortBySentiment()}
                    >
                      Sentiment
                      {sortConfig.key === 'sentiment' && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193'}
                        </span>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {threads.map(thread => (
                    <tr key={thread.id} className={thread.isOverdue ? 'overdue-row' : ''}>
                      <td>
                        <div className="employee-info">
                          <User className="employee-icon" />
                          <div>
                            <div className="employee-name">{thread.employeeName}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="client-email">
                          <Mail className="email-icon" />
                          {thread.clientEmail}
                        </div>
                      </td>
                      <td>
                        <div
                          className="subject-cell clickable"
                          onClick={() => handleViewThread(thread)}
                          title={`Click to view thread: ${thread.subject || 'No Subject'}`}
                        >
                          <FileText className="subject-icon" />
                          <span className="subject-text">
                            {thread.subject || 'No Subject'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${thread.status}`}>
                          {thread.status}
                        </span>
                      </td>
                      <td>
                        <div className="last-update-info">
                          <div className="update-date">
                            {thread.lastEmailDate ? format(new Date(thread.lastEmailDate), 'MMM dd, yyyy') : 'N/A'}
                          </div>
                          <div className="update-time">
                            {thread.lastEmailDate ? formatDistanceToNow(new Date(thread.lastEmailDate), { addSuffix: true }) : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="overdue-info">
                          <div className="overdue-time">
                            <Clock className="overdue-icon" />
                            {formatOverdueTime(thread.overdueBusinessHours)}
                          </div>
                          <div
                            className="overdue-indicator"
                            style={{ backgroundColor: getOverdueStatusColor(thread.overdueStatus) }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="sentiment-cell">
                          {thread.clientSentiment ? (
                            <span className={`sentiment-badge sentiment-${thread.clientSentiment}`}>
                              {thread.clientSentiment}
                            </span>
                          ) : (
                            <span className="sentiment-badge sentiment-neutral">Neutral</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleViewThread(thread)}
                          >
                            View Thread
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleCloseThread(thread)}
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  disabled={pagination.currentPage === 1}
                  onClick={() => {
                    console.log('Previous page clicked, current sortConfig:', sortConfig);
                    // Pass current sortConfig explicitly to avoid stale closure
                    fetchOverdueThreadsWithRefresh(pagination.currentPage - 1, true, sortConfig);
                  }}
                >
                  <ChevronLeft className="btn-icon" />
                  Previous
                </button>

                <div className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                  <span className="total-count">({pagination.total} total threads)</span>
                </div>

                <button
                  className="btn btn-secondary"
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => {
                    console.log('Next page clicked, current sortConfig:', sortConfig);
                    // Pass current sortConfig explicitly to avoid stale closure
                    fetchOverdueThreadsWithRefresh(pagination.currentPage + 1, true, sortConfig);
                  }}
                >
                  Next
                  <ChevronRight className="btn-icon" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Thread View Modal */}
      {isModalOpen && selectedThread && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Thread: {selectedThread.subject}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="thread-info">
                <div className="info-row">
                  <span className="info-label">Client Email:</span>
                  <span className="info-value">{selectedThread.clientEmail}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Assigned To:</span>
                  <span className="info-value">{selectedThread.employeeName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className="info-value">
                    <span className={`status-badge status-${selectedThread.status}`}>
                      {selectedThread.status}
                    </span>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Overdue Time:</span>
                  <span className="info-value">
                    <Clock className="info-icon" />
                    {formatOverdueTime(selectedThread.overdueBusinessHours)}
                  </span>
                </div>
              </div>

              <div className="conversations-section">
                <h3>Conversations</h3>
                {loadingConversations ? (
                  <div className="loading">Loading conversations...</div>
                ) : threadConversations.length > 0 ? (
                  <div className="conversations-list">
                    {threadConversations.map((conversation, index) => (
                      <div key={index} className="conversation-item">
                        <div className="conversation-header">
                          <div className="sender-info">
                            <span className="sender-name">
                              {conversation.from || conversation.sender || 'Unknown'}
                            </span>
                            <span className="sender-email">
                              {conversation.fromEmail || conversation.senderEmail || conversation.from || 'Unknown'}
                            </span>
                          </div>
                          <div className="conversation-date">
                            {conversation.date ? format(new Date(conversation.date), 'MMM dd, yyyy HH:mm') : 'N/A'}
                          </div>
                        </div>
                        <div className="conversation-body">
                          <div className="conversation-subject">
                            <strong>Subject:</strong> {conversation.subject || selectedThread.subject}
                          </div>
                          <div className="conversation-snippet">
                            {conversation.snippet || conversation.body || conversation.text || 'No content available'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-conversations">No conversations found for this thread.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SLAMonitoringDashboard;
